import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import NetInfo, { type NetInfoState } from '@react-native-community/netinfo';
import { isOfflineFromNetInfo } from '../utils/networkConnectivity';

type NetworkConnectivityContextValue = {
  isOffline: boolean;
  isChecking: boolean;
  retry: () => Promise<void>;
};

const NetworkConnectivityContext =
  createContext<NetworkConnectivityContextValue | null>(null);

export function NetworkConnectivityProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isOffline, setIsOffline] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const applyState = useCallback((state: NetInfoState) => {
    setIsOffline(isOfflineFromNetInfo(state));
  }, []);

  useEffect(() => {
    return NetInfo.addEventListener(applyState);
  }, [applyState]);

  const retry = useCallback(async () => {
    setIsChecking(true);
    try {
      const state = await NetInfo.refresh();
      applyState(state);
    } catch {
      setIsOffline(true);
    } finally {
      setIsChecking(false);
    }
  }, [applyState]);

  const value = useMemo(
    () => ({
      isOffline,
      isChecking,
      retry,
    }),
    [isChecking, isOffline, retry],
  );

  return (
    <NetworkConnectivityContext.Provider value={value}>
      {children}
    </NetworkConnectivityContext.Provider>
  );
}

export function useNetworkConnectivity(): NetworkConnectivityContextValue {
  const value = useContext(NetworkConnectivityContext);
  if (!value) {
    throw new Error(
      'useNetworkConnectivity must be used within NetworkConnectivityProvider',
    );
  }
  return value;
}
